import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumEmailDocumentGrouping } from "./enums/enumEmailDocumentGrouping.generated";

// Model Objects

export class GroupReadOnly extends ModelObject<GroupReadOnly> {

    /**
    * Outlet Group Code
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public code: string = "";
    private _mainLatitude: number | null = null;
    /**
    * Latitude
    */
    public get mainLatitude(): number | null { return this._mainLatitude }
    public set mainLatitude(value: number | null) {
      this._mainLatitude = value === undefined ? null : value;
    }
    private _mainLongitude: number | null = null;
    /**
    * Longitude
    */
    public get mainLongitude(): number | null { return this._mainLongitude }
    public set mainLongitude(value: number | null) {
      this._mainLongitude = value === undefined ? null : value;
    }
    private _placedOnHoldAt: Instant | null = null;
    /**
    * The time when Group was set as On Hold
    */
    public get placedOnHoldAt(): Instant | null { return this._placedOnHoldAt }
    public set placedOnHoldAt(value: Instant | null) {
      this._placedOnHoldAt = value === undefined ? null : value;
    }
    private _placedOnHoldByUserId: Guid | null = null;
    /**
    * User who set this Group as On Hold
    */
    public get placedOnHoldByUserId(): Guid | null { return this._placedOnHoldByUserId }
    public set placedOnHoldByUserId(value: Guid | null) {
      this._placedOnHoldByUserId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.code = jsonSerializedObject.code;
          this.mainLatitude = jsonSerializedObject.mainLatitude == null ? null : jsonSerializedObject.mainLatitude;
          this.mainLongitude = jsonSerializedObject.mainLongitude == null ? null : jsonSerializedObject.mainLongitude;
          this.placedOnHoldAt = jsonSerializedObject.placedOnHoldAt == null ? null : Instant.parse(jsonSerializedObject.placedOnHoldAt);
          this.placedOnHoldByUserId = jsonSerializedObject.placedOnHoldByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.placedOnHoldByUserId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.code = this.code;
        toRet.mainLatitude = this.mainLatitude == null ? null : this.mainLatitude;
        toRet.mainLongitude = this.mainLongitude == null ? null : this.mainLongitude;
        toRet.placedOnHoldAt = this.placedOnHoldAt == null ? null : this.placedOnHoldAt.toString();
        toRet.placedOnHoldByUserId = this.placedOnHoldByUserId == null ? null : this.placedOnHoldByUserId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "placedOnHoldByUserId", rule: (v: any) => v == null || !v.isEmpty() || "GroupReadOnly.PlacedOnHoldByUserId cannot be empty"},
    ];
}


export class Group extends ModelObjectWithDirtyCheck<Group> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    /**
    * Group Name
    * Not Nullable
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public telephoneNumber: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Main Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine1: string = "";
    /**
    * Main Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine2: string = "";
    /**
    * Main Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine3: string = "";
    /**
    * Main Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine4: string = "";
    /**
    * Main Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine5: string = "";
    /**
    * Main Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressLine6: string = "";
    /**
    * Main Address
    * Not Nullable
    * Max Length : 1000
    */
    public mainAddressPostcode: string = "";
    /**
    * VAT Registration No.
    * Not Nullable
    * Max Length : 1000
    */
    public vatNumber: string = "";
    /**
    * Bank Name
    * Not Nullable
    * Max Length : 1000
    */
    public bankName: string = "";
    /**
    * Bank Account No
    * Not Nullable
    * Max Length : 1000
    */
    public bankAccountNumber: string = "";
    /**
    * Bank Sort Code
    * Not Nullable
    * Max Length : 1000
    */
    public bankSortCode: string = "";
    /**
    * Send Invoices to the outlet rather than the group
    */
    public sendInvoiceToOutlet: boolean = false;
    /**
    * Email Invoices
    */
    public emailInvoices: boolean = false;
    /**
    * Email Address that receives invoices
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceEmailAddress: string = "";
    private _defaultInvoiceTemplateId: Guid | null = null;
    /**
    * Default Invoice Template Id
    */
    public get defaultInvoiceTemplateId(): Guid | null { return this._defaultInvoiceTemplateId }
    public set defaultInvoiceTemplateId(value: Guid | null) {
      this._defaultInvoiceTemplateId = value === undefined ? null : value;
    }
    private _invoiceDataExchangeTemplateId: Guid | null = null;
    /**
    * On optional invoice data exchange template that can be used to send a list of invoices to the group
    */
    public get invoiceDataExchangeTemplateId(): Guid | null { return this._invoiceDataExchangeTemplateId }
    public set invoiceDataExchangeTemplateId(value: Guid | null) {
      this._invoiceDataExchangeTemplateId = value === undefined ? null : value;
    }
    public readOnly: GroupReadOnly = new GroupReadOnly();
    /**
    * The number of invoice copies to print. (Not used if SendInvoiceToOutlet is true).
    * Not Negative
    */
    public invoiceCopiesToPrint: number = 0;
    private _deliveryServiceId: Guid | null = null;
    /**
    * Delivery Service, if any, to apply by default to orders for outlets in this group. May be overriden by outlet setting, but will have priority over courier / delivery area settings.
    */
    public get deliveryServiceId(): Guid | null { return this._deliveryServiceId }
    public set deliveryServiceId(value: Guid | null) {
      this._deliveryServiceId = value === undefined ? null : value;
    }
    /**
    * Accounts Department No (1-999)
    */
    public accountsDepartmentNumber: number = 0;
    /**
    * Identifies how multiple documents are grouped into pdfs and emails when many documents are emailed at the same time.
    */
    public emailDocumentGrouping: EnumEmailDocumentGrouping = EnumEmailDocumentGrouping.unknown;
    /**
    * If true, then this Group's sales code applies to all outlets in the group.
    */
    public useGroupSalesCodeForAllOutlets: boolean = false;
    private _salesCodeId: Guid | null = null;
    /**
    * Sales Code Id. if UseGroupSalesCodeForAllOutlets is true then this applies to all outlets in group.
    */
    public get salesCodeId(): Guid | null { return this._salesCodeId }
    public set salesCodeId(value: Guid | null) {
      this._salesCodeId = value === undefined ? null : value;
    }
    /**
    * Tax number for posting to accounts. Only shown for countries which require a Tax Number of invoice recipients such as Germany (Steuernummer).
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceRecipientTaxNumber: string = "";
    /**
    * If true, then this Group's product requirements apply to all outlets in the group.
    */
    public useGroupProductRequirementsForAllOutlets: boolean = false;
    /**
    * Product Requirements, eg minimum batch expiry lifetime.
    * Not Nullable
    * Max Length : 1000
    */
    public productRequirements: string = "";
    public onHoldReason: string = "";
    /**
    * True if PlacedOnHoldAt is not null
    */
    public onHold: boolean = false;
    /**
    * Credit Terms Set
    */
    public creditTermsSet: boolean = false;
    private _creditTermsId: Guid | null = null;
    /**
    * Credit Terms
    */
    public get creditTermsId(): Guid | null { return this._creditTermsId }
    public set creditTermsId(value: Guid | null) {
      this._creditTermsId = value === undefined ? null : value;
    }
    private _creditLimit: number | null = null;
    /**
    * Credit Limit
    * Not Negative
    */
    public get creditLimit(): number | null { return this._creditLimit }
    public set creditLimit(value: number | null) {
      this._creditLimit = value === undefined ? null : value;
    }
    private _creditReviewDate: LocalDate | null = null;
    /**
    * Credit Review Date
    */
    public get creditReviewDate(): LocalDate | null { return this._creditReviewDate }
    public set creditReviewDate(value: LocalDate | null) {
      this._creditReviewDate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.name = jsonSerializedObject.name;
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.mainAddressLine1 = jsonSerializedObject.mainAddressLine1;
          this.mainAddressLine2 = jsonSerializedObject.mainAddressLine2;
          this.mainAddressLine3 = jsonSerializedObject.mainAddressLine3;
          this.mainAddressLine4 = jsonSerializedObject.mainAddressLine4;
          this.mainAddressLine5 = jsonSerializedObject.mainAddressLine5;
          this.mainAddressLine6 = jsonSerializedObject.mainAddressLine6;
          this.mainAddressPostcode = jsonSerializedObject.mainAddressPostcode;
          this.vatNumber = jsonSerializedObject.vatNumber;
          this.bankName = jsonSerializedObject.bankName;
          this.bankAccountNumber = jsonSerializedObject.bankAccountNumber;
          this.bankSortCode = jsonSerializedObject.bankSortCode;
          this.sendInvoiceToOutlet = jsonSerializedObject.sendInvoiceToOutlet;
          this.emailInvoices = jsonSerializedObject.emailInvoices;
          this.invoiceEmailAddress = jsonSerializedObject.invoiceEmailAddress;
          this.defaultInvoiceTemplateId = jsonSerializedObject.defaultInvoiceTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultInvoiceTemplateId);
          this.invoiceDataExchangeTemplateId = jsonSerializedObject.invoiceDataExchangeTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.invoiceDataExchangeTemplateId);
          this.readOnly = new GroupReadOnly(jsonSerializedObject.readOnly);
          this.invoiceCopiesToPrint = jsonSerializedObject.invoiceCopiesToPrint;
          this.deliveryServiceId = jsonSerializedObject.deliveryServiceId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryServiceId);
          this.accountsDepartmentNumber = jsonSerializedObject.accountsDepartmentNumber;
          this.emailDocumentGrouping = EnumEmailDocumentGrouping.getByValue(jsonSerializedObject.emailDocumentGrouping, this.emailDocumentGrouping);
          this.useGroupSalesCodeForAllOutlets = jsonSerializedObject.useGroupSalesCodeForAllOutlets;
          this.salesCodeId = jsonSerializedObject.salesCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesCodeId);
          this.invoiceRecipientTaxNumber = jsonSerializedObject.invoiceRecipientTaxNumber;
          this.useGroupProductRequirementsForAllOutlets = jsonSerializedObject.useGroupProductRequirementsForAllOutlets;
          this.productRequirements = jsonSerializedObject.productRequirements;
          this.onHoldReason = jsonSerializedObject.onHoldReason;
          this.onHold = jsonSerializedObject.onHold;
          this.creditTermsSet = jsonSerializedObject.creditTermsSet;
          this.creditTermsId = jsonSerializedObject.creditTermsId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.creditTermsId);
          this.creditLimit = jsonSerializedObject.creditLimit == null ? null : jsonSerializedObject.creditLimit;
          this.creditReviewDate = jsonSerializedObject.creditReviewDate == null ? null : LocalDate.parse(jsonSerializedObject.creditReviewDate);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.name = this.name;
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.emailAddress = this.emailAddress;
        toRet.mainAddressLine1 = this.mainAddressLine1;
        toRet.mainAddressLine2 = this.mainAddressLine2;
        toRet.mainAddressLine3 = this.mainAddressLine3;
        toRet.mainAddressLine4 = this.mainAddressLine4;
        toRet.mainAddressLine5 = this.mainAddressLine5;
        toRet.mainAddressLine6 = this.mainAddressLine6;
        toRet.mainAddressPostcode = this.mainAddressPostcode;
        toRet.vatNumber = this.vatNumber;
        toRet.bankName = this.bankName;
        toRet.bankAccountNumber = this.bankAccountNumber;
        toRet.bankSortCode = this.bankSortCode;
        toRet.sendInvoiceToOutlet = this.sendInvoiceToOutlet;
        toRet.emailInvoices = this.emailInvoices;
        toRet.invoiceEmailAddress = this.invoiceEmailAddress;
        toRet.defaultInvoiceTemplateId = this.defaultInvoiceTemplateId == null ? null : this.defaultInvoiceTemplateId.toString();
        toRet.invoiceDataExchangeTemplateId = this.invoiceDataExchangeTemplateId == null ? null : this.invoiceDataExchangeTemplateId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.invoiceCopiesToPrint = this.invoiceCopiesToPrint;
        toRet.deliveryServiceId = this.deliveryServiceId == null ? null : this.deliveryServiceId.toString();
        toRet.accountsDepartmentNumber = this.accountsDepartmentNumber;
        toRet.emailDocumentGrouping = this.emailDocumentGrouping.value;
        toRet.useGroupSalesCodeForAllOutlets = this.useGroupSalesCodeForAllOutlets;
        toRet.salesCodeId = this.salesCodeId == null ? null : this.salesCodeId.toString();
        toRet.invoiceRecipientTaxNumber = this.invoiceRecipientTaxNumber;
        toRet.useGroupProductRequirementsForAllOutlets = this.useGroupProductRequirementsForAllOutlets;
        toRet.productRequirements = this.productRequirements;
        toRet.onHoldReason = this.onHoldReason;
        toRet.onHold = this.onHold;
        toRet.creditTermsSet = this.creditTermsSet;
        toRet.creditTermsId = this.creditTermsId == null ? null : this.creditTermsId.toString();
        toRet.creditLimit = this.creditLimit == null ? null : this.creditLimit;
        toRet.creditReviewDate = this.creditReviewDate == null ? null : this.creditReviewDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Group.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "telephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "TelephoneNumber has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "mainAddressLine1", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine1 has too many characters (1000)"},
            {field: "mainAddressLine2", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine2 has too many characters (1000)"},
            {field: "mainAddressLine3", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine3 has too many characters (1000)"},
            {field: "mainAddressLine4", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine4 has too many characters (1000)"},
            {field: "mainAddressLine5", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine5 has too many characters (1000)"},
            {field: "mainAddressLine6", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressLine6 has too many characters (1000)"},
            {field: "mainAddressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "MainAddressPostcode has too many characters (1000)"},
            {field: "vatNumber", rule: (v: any) => v == null || v.length <= 1000 || "VatNumber has too many characters (1000)"},
            {field: "bankName", rule: (v: any) => v == null || v.length <= 1000 || "BankName has too many characters (1000)"},
            {field: "bankAccountNumber", rule: (v: any) => v == null || v.length <= 1000 || "BankAccountNumber has too many characters (1000)"},
            {field: "bankSortCode", rule: (v: any) => v == null || v.length <= 1000 || "BankSortCode has too many characters (1000)"},
            {field: "invoiceEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceEmailAddress has too many characters (1000)"},
            {field: "defaultInvoiceTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "Group.DefaultInvoiceTemplateId cannot be empty"},
            {field: "invoiceDataExchangeTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "Group.InvoiceDataExchangeTemplateId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "invoiceCopiesToPrint", rule: (v: any) => v == null || v >= 0 || "InvoiceCopiesToPrint is not greater than minimum allowed value: 0"},
            {field: "invoiceCopiesToPrint", rule: (v: any) => v == null || v <= 2147483647 || "InvoiceCopiesToPrint is above maximum allowed value: 2147483647"},
            {field: "deliveryServiceId", rule: (v: any) => v == null || !v.isEmpty() || "Group.DeliveryServiceId cannot be empty"},
            {field: "accountsDepartmentNumber", rule: (v: any) => v == null || v >= -2147483648 || "AccountsDepartmentNumber is not greater than minimum allowed value: -2147483648"},
            {field: "accountsDepartmentNumber", rule: (v: any) => v == null || v <= 2147483647 || "AccountsDepartmentNumber is above maximum allowed value: 2147483647"},
            {field: "salesCodeId", rule: (v: any) => v == null || !v.isEmpty() || "Group.SalesCodeId cannot be empty"},
            {field: "invoiceRecipientTaxNumber", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceRecipientTaxNumber has too many characters (1000)"},
            {field: "productRequirements", rule: (v: any) => v == null || v.length <= 1000 || "ProductRequirements has too many characters (1000)"},
            {field: "onHoldReason", rule: (v: any) => v == null || v.length <= 1000 || "OnHoldReason has too many characters (1000)"},
            {field: "creditTermsId", rule: (v: any) => v == null || !v.isEmpty() || "Group.CreditTermsId cannot be empty"},
    ];
}


export class GroupContact extends ModelObjectWithDirtyCheck<GroupContact> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Group Id
    */
    public groupId: Guid = Guid.createEmpty();
    /**
    * Forename
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public forename: string = "";
    /**
    * Surname
    * Not Nullable
    * Max Length : 1000
    */
    public surname: string = "";
    /**
    * Salutation
    * Not Nullable
    * Max Length : 1000
    */
    public salutation: string = "";
    /**
    * JobTitle
    * Not Nullable
    * Max Length : 1000
    */
    public jobTitle: string = "";
    /**
    * Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public phoneNumber: string = "";
    /**
    * Phone Extension
    * Not Nullable
    * Max Length : 1000
    */
    public phoneExtension: string = "";
    /**
    * Mobile Phone Number
    * Not Nullable
    * Max Length : 1000
    */
    public mobileNumber: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Address Line 1
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine1: string = "";
    /**
    * Address Line 2
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine2: string = "";
    /**
    * Address Line 3
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine3: string = "";
    /**
    * Address Line 4
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine4: string = "";
    /**
    * Address Line 5
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine5: string = "";
    /**
    * Address Line 6
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine6: string = "";
    /**
    * Postcode
    * Not Nullable
    * Max Length : 1000
    */
    public postcode: string = "";
    /**
    * Receives invoices
    */
    public areasInvoice: boolean = false;
    /**
    * Deals with orders
    */
    public areasOrders: boolean = false;
    /**
    * Receives delivery notes
    */
    public areasDeliveries: boolean = false;
    /**
    * Received marketing communication
    */
    public areasMarketing: boolean = false;
    /**
    * Deals with account queries
    */
    public areasAccounts: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.groupId = Guid.parseOrEmpty(jsonSerializedObject.groupId);
          this.forename = jsonSerializedObject.forename;
          this.surname = jsonSerializedObject.surname;
          this.salutation = jsonSerializedObject.salutation;
          this.jobTitle = jsonSerializedObject.jobTitle;
          this.phoneNumber = jsonSerializedObject.phoneNumber;
          this.phoneExtension = jsonSerializedObject.phoneExtension;
          this.mobileNumber = jsonSerializedObject.mobileNumber;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.notes = jsonSerializedObject.notes;
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.addressLine5 = jsonSerializedObject.addressLine5;
          this.addressLine6 = jsonSerializedObject.addressLine6;
          this.postcode = jsonSerializedObject.postcode;
          this.areasInvoice = jsonSerializedObject.areasInvoice;
          this.areasOrders = jsonSerializedObject.areasOrders;
          this.areasDeliveries = jsonSerializedObject.areasDeliveries;
          this.areasMarketing = jsonSerializedObject.areasMarketing;
          this.areasAccounts = jsonSerializedObject.areasAccounts;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.groupId = this.groupId.toString();
        toRet.forename = this.forename;
        toRet.surname = this.surname;
        toRet.salutation = this.salutation;
        toRet.jobTitle = this.jobTitle;
        toRet.phoneNumber = this.phoneNumber;
        toRet.phoneExtension = this.phoneExtension;
        toRet.mobileNumber = this.mobileNumber;
        toRet.emailAddress = this.emailAddress;
        toRet.notes = this.notes;
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.addressLine5 = this.addressLine5;
        toRet.addressLine6 = this.addressLine6;
        toRet.postcode = this.postcode;
        toRet.areasInvoice = this.areasInvoice;
        toRet.areasOrders = this.areasOrders;
        toRet.areasDeliveries = this.areasDeliveries;
        toRet.areasMarketing = this.areasMarketing;
        toRet.areasAccounts = this.areasAccounts;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "GroupContact.Id cannot be empty"},
            {field: "groupId", rule: (v: any) => !v.isEmpty() || "GroupContact.GroupId cannot be empty"},
            {field: "forename", rule: (v: any) => v == null || v.length !== 0 || "Forename cannot be empty"},
            {field: "forename", rule: (v: any) => v == null || v.length <= 1000 || "Forename has too many characters (1000)"},
            {field: "surname", rule: (v: any) => v == null || v.length <= 1000 || "Surname has too many characters (1000)"},
            {field: "salutation", rule: (v: any) => v == null || v.length <= 1000 || "Salutation has too many characters (1000)"},
            {field: "jobTitle", rule: (v: any) => v == null || v.length <= 1000 || "JobTitle has too many characters (1000)"},
            {field: "phoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "PhoneNumber has too many characters (1000)"},
            {field: "phoneExtension", rule: (v: any) => v == null || v.length <= 1000 || "PhoneExtension has too many characters (1000)"},
            {field: "mobileNumber", rule: (v: any) => v == null || v.length <= 1000 || "MobileNumber has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "addressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine1 has too many characters (1000)"},
            {field: "addressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine2 has too many characters (1000)"},
            {field: "addressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine3 has too many characters (1000)"},
            {field: "addressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine4 has too many characters (1000)"},
            {field: "addressLine5", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine5 has too many characters (1000)"},
            {field: "addressLine6", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine6 has too many characters (1000)"},
            {field: "postcode", rule: (v: any) => v == null || v.length <= 1000 || "Postcode has too many characters (1000)"},
    ];
}


export class GroupLatestCreditBalance extends ModelObject<GroupLatestCreditBalance> {

    private _creditBalance: number | null = null;
    /**
    * Credit balance from the accounts package. Captured by system not user editable.
    * Not Negative
    */
    public get creditBalance(): number | null { return this._creditBalance }
    public set creditBalance(value: number | null) {
      this._creditBalance = value === undefined ? null : value;
    }
    private _creditBalanceExpiresAt: Instant | null = null;
    /**
    * The time when the credit balance will expire and need updating from the accounts package. Captured by system not user editable.
    */
    public get creditBalanceExpiresAt(): Instant | null { return this._creditBalanceExpiresAt }
    public set creditBalanceExpiresAt(value: Instant | null) {
      this._creditBalanceExpiresAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.creditBalance = jsonSerializedObject.creditBalance == null ? null : jsonSerializedObject.creditBalance;
          this.creditBalanceExpiresAt = jsonSerializedObject.creditBalanceExpiresAt == null ? null : Instant.parse(jsonSerializedObject.creditBalanceExpiresAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.creditBalance = this.creditBalance == null ? null : this.creditBalance;
        toRet.creditBalanceExpiresAt = this.creditBalanceExpiresAt == null ? null : this.creditBalanceExpiresAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class GroupCreditBalance extends ModelObject<GroupCreditBalance> {

    public groupId: Guid = Guid.createEmpty();
    private _creditBalance: number | null = null;
    public get creditBalance(): number | null { return this._creditBalance }
    public set creditBalance(value: number | null) {
      this._creditBalance = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.groupId = Guid.parseOrEmpty(jsonSerializedObject.groupId);
          this.creditBalance = jsonSerializedObject.creditBalance == null ? null : jsonSerializedObject.creditBalance;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.groupId = this.groupId.toString();
        toRet.creditBalance = this.creditBalance == null ? null : this.creditBalance;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "groupId", rule: (v: any) => !v.isEmpty() || "GroupCreditBalance.GroupId cannot be empty"},
    ];
}


export class GroupService extends ServiceBase {

  public async getAllGroups(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Group)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.groups.map((v: any) => new Group(v));;
    return this.makeJsonRequest("group/v1/getAllGroups", req, false, extractor, opts);
  };

  public async getGroups(groupIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Group)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupIds:groupIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.group.map((v: any) => new Group(v));;
    return this.makeJsonRequest("group/v1/getGroups", req, false, extractor, opts);
  };

  public async getGroup(groupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Group> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupId:groupId.toString(),
    };
    const extractor = (response:any) => new Group(response.group);;
    return this.makeJsonRequest("group/v1/getGroup", req, false, extractor, opts);
  };

  public async createGroup(group: Group, groupCode: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    group:group.toJsonSerializedObject(),
    groupCode:groupCode,
    };
    const extractor = null;
    return this.makeJsonRequest("group/v1/createGroup", req, true, extractor, opts);
  };

  public async updateGroup(groupId: Guid, expectedVersion: number | null, group: Group, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupId:groupId.toString(),
    expectedVersion:expectedVersion == null ? null : expectedVersion,
    group:group.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("group/v1/updateGroup", req, true, extractor, opts);
  };

  public async setGroupIsHidden(groupId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupId:groupId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("group/v1/setGroupIsHidden", req, true, extractor, opts);
  };

  public async getGroupLatestCreditBalance(groupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<GroupLatestCreditBalance> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupId:groupId.toString(),
    };
    const extractor = (response:any) => new GroupLatestCreditBalance(response.latestCreditBalance);;
    return this.makeJsonRequest("group/v1/getGroupLatestCreditBalance", req, false, extractor, opts);
  };

  public async updateGroupCreditBalances(groupCreditBalances: readonly GroupCreditBalance[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupCreditBalances:groupCreditBalances.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("group/v1/updateGroupCreditBalances", req, true, extractor, opts);
  };

  public async getGroupContacts(groupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(GroupContact)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupId:groupId.toString(),
    };
    const extractor = (response:any) => response.contacts.map((v: any) => new GroupContact(v));;
    return this.makeJsonRequest("group/v1/getGroupContacts", req, false, extractor, opts);
  };

  public async getMultipleGroupContacts(groupIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(GroupContact)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupIds:groupIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.contacts.map((v: any) => new GroupContact(v));;
    return this.makeJsonRequest("group/v1/getMultipleGroupContacts", req, false, extractor, opts);
  };

  public async saveGroupContacts(contacts: readonly GroupContact[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    contacts:contacts.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("group/v1/saveGroupContacts", req, true, extractor, opts);
  };

  public async deleteGroupContacts(contactIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    contactIds:contactIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("group/v1/deleteGroupContacts", req, true, extractor, opts);
  };

  public async getAllGroupContacts(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(GroupContact)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.groupContacts.map((v: any) => new GroupContact(v));;
    return this.makeJsonRequest("group/v1/getAllGroupContacts", req, false, extractor, opts);
  };

  public async generateGroupCode(groupName: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    groupName:groupName,
    };
    const extractor = (response:any) => response.code;;
    return this.makeJsonRequest("group/v1/generateGroupCode", req, false, extractor, opts);
  };
}

export const groupService = new GroupService();